import React, { useState, useEffect } from 'react';
import {
  Link, useLocation, useParams, useNavigate,
} from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap5';

import routes from 'app/routes';
import ExternalLink from 'components/Widgets/ExternalLink';
import IconWithText from 'components/Widgets/IconWithText';
import { primary } from 'components/new_ui/Colors';
import ProgramInfo from 'components/Members/Schedule/Widgets/ProgramInfo/Index';

import useStep from 'components/Common/Members/Hooks/useStep';
import useLesson from 'components/Common/Members/Hooks/useLesson';

import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';

import confirmWithoutCancel from 'components/new_ui/Alerts/confirmWithoutCancel';

import Alert from 'components/new_ui/Alerts/alert';
import confirmAlert from 'components/new_ui/Alerts/confirm';

import { useContext as useUsersContext } from 'components/Users/Context';

import { useContext as useHomeContext } from 'components/Members/Home/Context';
import Breadcrumbs from 'components/new_ui/Breadcrumbs/Index';
import Actions from '../../Actions';

export default function Index() {
  const { state: stateLocation } = useLocation();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { state: userState, actions: userActions, dispatch: userDispatch } = useUsersContext();

  const brandingAvailable = userState.user?.attributes?.branding_available;

  const { stepId } = useParams();

  const step = stateLocation?.step || useStep(stepId);
  const source = stateLocation?.source || 'sessions';
  const sourceId = stateLocation?.sourceId;
  const selectedTime = stateLocation?.selectedTime;
  const session = stateLocation?.session || useLesson(stepId);

  const isCancel = session && session?.attributes?.status?.includes('Undelivered');
  const isWaiting = session && session?.attributes?.status?.includes('Waiting');
  const isCompleted = session && session?.attributes?.status?.includes('Completed');

  const isPossibleScheduleReschedule = step?.attributes?.actions?.some(
    (item) => item.action === 'reschedule' || item.action === 'schedule',
  );
  const isPossibleCancel = step?.attributes?.actions?.some((item) => item.action === 'cancel');

  const homeContext = useHomeContext();

  const member = userState.user?.attributes?.specific;

  const breadcrumbs = [
    {
      name: 'Home',
      path: '/members/home',
    },
    {
      name: 'Schedule',
    },
  ];

  const onClose = () => {
    setLoading(true);

    let url = routes.members.schedule.index;
    if (source === 'program') {
      url = routes.members.programs.show(sourceId);
    }

    if (member && member.ai_enabled) {
      url = '/members/home';
    }

    document.location.href = url;
  };

  const scheduleSession = () => {
    setLoading(true);

    const params = {
      start_at: selectedTime,
      zoom_id: session?.attributes?.zoom_id,
    };

    let url = routes.members.schedule.index;
    if (source === 'program') {
      url = routes.members.programs.show(sourceId);
    }

    if (member && member.ai_enabled) {
      url = '/members/home';
    }

    const processError = (errors) => {
      setLoading(false);
      Alert(
        {
          title: 'Error',
          text: errors.errors[0].detail,
          confirmButtonText: 'Close',
        },
      );
    };

    if (step?.attributes?.session_id) {
      Actions.reschedule(step, params, () => {
        setLoading(false);

        confirmWithoutCancel({
          title: 'Reschedule Session',
          text: `You have successfully rescheduled your session. 
          Your coach will be notified about the change.`,
          confirmButtonText: 'Close',
        }, () => {
          document.location.href = url;
        });
      }, (errors) => {
        processError(errors);
      });
    } else {
      Actions.schedule(step, params, () => {
        setLoading(false);

        confirmWithoutCancel({
          icon: 'success',
          iconColor: primary,
          title: 'Schedule Session',
          text: `Thank you for scheduling a session with your coach! LeggUP will notify 
        the coach about the time slot that you selected for your session.`,
          confirmButtonText: 'Close',
        }, () => {
          document.location.href = url;
        });
      }, (errors) => {
        processError(errors);
      });
    }
  };

  const onClickSessionCancel = () => {
    Actions.cancelType(step, (data) => {
      let text = 'Do you want to cancel this session? The session will disappear from your Calendar.';

      if (data.type === 'smaller_24_hours') {
        text = 'Less then 24 hours to start. Do you really want to cancel the session?';
      }

      confirmAlert({
        title: 'Cancel Session',
        text,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      }, () => {
        setLoading(true);
        Actions.cancel(step, (sessionData) => {
          setLoading(false);
          userActions.load(userDispatch);

          navigate(routes.members.steps.schedule(step.id), {
            state: {
              step,
              source,
              sourceId,
              selectedTime,
              session: sessionData,
            },
            replace: true,
          });
        }, (errors) => {
          setLoading(false);
          Alert(
            {
              title: 'Error',
              text: errors.errors[0].detail,
              confirmButtonText: 'Close',
            },
          );
        });
      });
    });
  };

  useEffect(() => {
    if (session && session.attributes.later_cancel) {
      Alert({
        text: `Your session is scheduled to start within 24 hours. 
        Please note that according to LeggUP policy you can only cancel 
        the session within this time period.`,
      });
    }
  }, [session]);

  if (!step || !session) {
    return (
      <div className="content">
        <section>
          <LoadingPlaceholder text="Loading..." />
        </section>
      </div>
    );
  }

  return (
    <div className="content">
      {homeContext && <Breadcrumbs breadcrumbs={breadcrumbs} />}
      <section>
        <h1>
          Session:
          {' '}
          {step.attributes.title}
        </h1>
      </section>

      <section>
        <Row>
          <Col lg={6}>
            <ProgramInfo record={step} session={session} />
          </Col>

          <Col lg={6}>
            <h5 className="mb-4">This is your meeting information</h5>

            {(isCancel || (isWaiting && session.attributes.zoom_id === '')) && (
              <>
                <p>
                  This session has been cancelled on
                  {' '}
                  {session.attributes.cancel_at_formatted}
                </p>
                <p>
                  The meeting information is not available any more.
                  Please schedule this session again to get actual meeting information.
                </p>
              </>
            )}
            {!isCancel && !((isWaiting && session.attributes.zoom_id === '')) && (
              <>
                <IconWithText
                  iconClassName="video-icon"
                  iconStyle={{ maskSize: '60%' }}
                  iconBg="medium-bg"
                  iconBgStyle={{ backgroundColor: brandingAvailable ? primary : '#3195EF' }}
                  wrapperClassName="mb-4"
                >
                  <>
                    <ExternalLink
                      variant={null}
                      href={session.attributes.zoom_link}
                    >
                      {session.attributes.zoom_link}
                    </ExternalLink>

                    <p className="mt-3">
                      Meeting ID:
                      {' '}
                      {session.attributes.zoom_id}
                    </p>
                  </>
                </IconWithText>

                {session.attributes.zoom_options.phone && (
                <>
                  <p>
                    Dial by your location:
                  </p>

                  <ul className="list-inline">
                    {session.attributes.zoom_options.phone.map((phoneInfo) => (
                      <li key={phoneInfo.phone}>
                        <span className="fw-semibold">{phoneInfo.phone}</span>
                        {' - '}
                        {phoneInfo.country}
                        {' / '}
                        {phoneInfo.city}
                      </li>
                    ))}
                  </ul>
                </>
                )}

                <p>
                  Find your local number:
                  {' '}
                  <ExternalLink
                    variant={null}
                    href="https://zoom.us/u/abVFs1Rsg4"
                  >
                    https://zoom.us/u/abVFs1Rsg4
                  </ExternalLink>
                </p>
              </>
            )}

          </Col>
        </Row>
      </section>

      <footer className={`d-flex ${isCancel || isWaiting
          || isCompleted ? 'justify-content-end' : 'justify-content-between'} `}
      >
        {!isCancel && !isWaiting && !isCompleted && isPossibleScheduleReschedule && (
        <Link
          to={routes.members.steps.schedule(step.id)}
          state={{
            step,
            source,
            sourceId,
            selectedTime,
            session,
          }}
          className={`btn btn-outline-primary ${loading ? 'disabled' : ''}`}
        >
          Previous
        </Link>
        )}
        { (isCancel || isWaiting || isCompleted || !isPossibleScheduleReschedule) && (
          <Button
            onClick={onClose}
            className={loading ? 'disabled' : ''}
          >
            Close
          </Button>
        )}
        {!isPossibleScheduleReschedule && isPossibleCancel && !isCancel && !isWaiting && !isCompleted && (
        <Button
          onClick={onClickSessionCancel}
          variant="outline-primary"
          className={loading ? 'disabled' : ''}
        >
          Cancel Session
        </Button>
        )}
        {!isCancel && !isWaiting && !isCompleted && isPossibleScheduleReschedule && (
        <Button
          onClick={scheduleSession}
          className={loading ? 'disabled' : ''}
        >
          {loading && <LoadingPlaceholder placement="inline" text=" " />}
          Confirm
        </Button>
        )}
      </footer>
    </div>
  );
}
